/**
 * Sprint 18 B3: Capacity Optimizer — live staff heatmap + review-first suggestions
 */
import { AlertTriangle, Brain, CalendarClock, Users } from "lucide-react";
import { useApi } from "@/hooks/use-api";
import { Skeleton } from "@/components/skeleton";
import { formatDateTime } from "@/lib/i18n";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

interface CapacitySuggestion {
  id: string;
  type: "promote_slot" | "reduce_staff" | "add_staff" | "adjust_hours" | "block_slot";
  title: string;
  description: string;
  impact: string;
  dayOfWeek: number;
  hour: number;
  confidence: number;
}

interface CapacityData {
  hours: number[];
  staff: Array<{
    id: string;
    name: string;
    role: string;
    utilization: number[];
    averageUtilization: number;
    scheduledHoursPerWeek: number;
  }>;
  suggestions: CapacitySuggestion[];
  overallUtilization: number;
  analyzedAt: string;
  lookbackDays: number;
}

function heatColor(value: number): string {
  if (value >= 80) return "bg-[var(--color-error)]/60";
  if (value >= 60) return "bg-[var(--color-warning)]/50";
  if (value >= 30) return "bg-[var(--color-success)]/40";
  if (value > 0) return "bg-[var(--color-success)]/15";
  return "bg-[var(--color-surface)]";
}

function formatSuggestionWindow(dayOfWeek: number, hour: number): string {
  const dayLabel = DAY_LABELS[dayOfWeek] ?? "Day";
  return `${dayLabel} ${String(hour).padStart(2, "0")}:00`;
}

export default function BookingCapacityPage() {
  const { data, isLoading, error } = useApi<CapacityData>("/crm/bookings/capacity");

  const d = data ?? {
    hours: [],
    staff: [],
    suggestions: [],
    overallUtilization: 0,
    analyzedAt: "",
    lookbackDays: 28,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Capacity Optimizer</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">Live staff utilization heatmap with review-first optimization suggestions.</p>
          {d.analyzedAt ? (
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              Based on the last {d.lookbackDays} days of booking history. Refreshed {formatDateTime(d.analyzedAt)}.
            </p>
          ) : null}
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-[var(--color-foreground)]">{d.overallUtilization}%</div>
          <div className="text-xs text-[var(--color-muted-foreground)]">Overall utilization</div>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-48 rounded-xl" />
      ) : error ? (
        <div className="rounded-xl border border-[var(--color-error)]/30 bg-[var(--color-error)]/10 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-[var(--color-error)]" />
            <div>
              <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Capacity data is unavailable</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{error}</p>
            </div>
          </div>
        </div>
      ) : d.staff.length === 0 ? (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
          <div className="flex items-start gap-3">
            <CalendarClock className="mt-0.5 h-5 w-5 text-[var(--color-muted-foreground)]" />
            <div>
              <h2 className="text-sm font-semibold text-[var(--color-foreground)]">No active staff schedules found</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Capacity optimization only renders after active resources and weekly schedules exist in the service delivery runtime.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 overflow-x-auto">
            <div className="min-w-[720px]">
              <div className="mb-1 flex">
                <div className="w-32 shrink-0" />
                {d.hours.map((hour) => (
                  <div key={hour} className="flex-1 text-center text-xs text-[var(--color-muted-foreground)]">{hour}:00</div>
                ))}
                <div className="w-20 shrink-0 text-right text-xs text-[var(--color-muted-foreground)]">Avg</div>
              </div>
              {d.staff.map((member) => (
                <div key={member.id} className="stagger-item mt-1 flex items-center gap-1">
                  <div className="w-32 shrink-0">
                    <div className="flex items-center gap-1 truncate text-sm text-[var(--color-foreground)]">
                      <Users className="h-3.5 w-3.5 text-[var(--color-muted-foreground)]" />
                      {member.name}
                    </div>
                    <div className="truncate text-[11px] text-[var(--color-muted-foreground)]">
                      {member.role} · {member.scheduledHoursPerWeek}h/week
                    </div>
                  </div>
                  {member.utilization.map((value, index) => (
                    <div
                      key={`${member.id}-${d.hours[index] ?? index}`}
                      className={`flex-1 h-8 rounded ${heatColor(value)} transition-colors`}
                      title={`${member.name} ${d.hours[index] ?? index}:00 — ${value}% utilized`}
                    />
                  ))}
                  <div className="w-20 shrink-0 text-right text-sm font-medium text-[var(--color-foreground)]">
                    {member.averageUtilization}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {d.suggestions.length > 0 ? (
            <div className="rounded-xl border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] p-5">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--color-ai)]">
                <Brain className="h-4 w-4" /> Optimization Suggestions
              </h2>
              <p className="mb-4 text-xs text-[var(--color-muted-foreground)]">
                Heuristic guidance from booking history only. Current cards cover fill-gap, staffing, and no-show actions; waitlist moves and revenue-optimal holds are deferred.
              </p>
              <div className="space-y-3">
                {d.suggestions.slice(0, 6).map((suggestion) => (
                  <div key={suggestion.id} className="stagger-item rounded-lg border border-[var(--color-ai-border)]/40 bg-[var(--color-background)]/70 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-[var(--color-foreground)]">{suggestion.title}</h3>
                        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                          {formatSuggestionWindow(suggestion.dayOfWeek, suggestion.hour)} · {suggestion.confidence}% confidence
                        </p>
                      </div>
                      <span className="rounded-full bg-[var(--color-ai)]/10 px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-[var(--color-ai)]">
                        {suggestion.type.replaceAll("_", " ")}
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-[var(--color-foreground)]">{suggestion.description}</p>
                    <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">{suggestion.impact}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
